import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Loader2 } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { Slider } from "./ui/slider";

const BAR_COUNT = 32;

const RadioPlayer = () => {
  const {
    isPlaying, isLoading, togglePlay, volume, isMuted,
    setVolume, toggleMute, error, station, nowPlaying, analyserRef,
  } = usePlayer();

  const [levels, setLevels] = useState(() => Array(BAR_COUNT).fill(0.08));
  const rafRef = useRef(null);
  const dataRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const tick = () => {
      const analyser = analyserRef && analyserRef.current;
      if (isPlaying && analyser) {
        const bins = analyser.frequencyBinCount;
        if (!dataRef.current || dataRef.current.length !== bins) {
          dataRef.current = new Uint8Array(bins);
        }
        analyser.getByteFrequencyData(dataRef.current);
        const usable = Math.min(bins, 40); // focus on low/mid range where music lives
        const next = new Array(BAR_COUNT);
        for (let i = 0; i < BAR_COUNT; i++) {
          const idx = Math.floor((i / BAR_COUNT) * usable);
          const v = dataRef.current[idx] / 255;
          // mild progressive gain so higher bars stay active without saturating everything
          const gain = 0.7 + (i / BAR_COUNT) * 0.9;
          const val = Math.min(1, Math.pow(v, 1.5) * gain);
          next[i] = Math.max(0.06, val);
        }
        if (mounted) setLevels(next);
      } else if (isPlaying) {
        // Synthetic, lively animation while playing (no audio analyser available).
        const t = performance.now() / 260;
        const next = Array.from({ length: BAR_COUNT }, (_, i) => {
          const v = 0.2 + 0.4 * Math.abs(Math.sin(t + i * 0.55) * Math.cos(t * 0.6 + i * 0.3));
          return Math.min(1, Math.max(0.06, v));
        });
        if (mounted) setLevels(next);
      } else {
        const t = performance.now() / 700;
        const next = Array.from({ length: BAR_COUNT }, (_, i) => 0.08 + 0.05 * Math.abs(Math.sin(t + i * 0.4)));
        if (mounted) setLevels(next);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      mounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isPlaying, analyserRef]);

  return (
    <div className="relative w-full max-w-md mx-auto">
      {/* soft halo */}
      <div className="absolute -inset-6 rounded-[2.5rem] bg-white opacity-20 blur-3xl" />

      <div className="relative rounded-[2rem] bg-[#4288F5] border border-white/15 p-4 sm:p-5 shadow-2xl shadow-blue-900/25">
        <div className="flex items-center gap-4 sm:gap-5">
          <div className="relative shrink-0">
            <div className={`absolute inset-0 rounded-2xl bg-white ${isPlaying ? "animate-ping-slow" : ""}`} />
            <div className="relative h-20 w-20 sm:h-24 sm:w-24 rounded-2xl bg-gradient-to-br from-[#2E7DF7] to-[#1E6BE6] flex items-center justify-center shadow-lg shadow-blue-900/30 ring-1 ring-white/40 overflow-hidden">
              {nowPlaying.artwork ? (
                <img
                  key={nowPlaying.artwork}
                  src={nowPlaying.artwork}
                  alt={nowPlaying.title}
                  className="w-full h-full object-cover"
                  data-testid="player-artwork"
                  onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                />
              ) : null}
              <div
                className="absolute inset-0 flex items-center justify-center p-4"
                style={{ display: nowPlaying.artwork ? "none" : "flex" }}
              >
                <img src={station.logoFull} alt={station.name} className="h-full w-full object-contain" />
              </div>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-white">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
              </span>
              Live
            </span>
            <h3 className="mt-2 font-heading text-lg sm:text-xl font-bold text-white">{station.name}</h3>
            <p className="text-sm text-white/80">{nowPlaying.title}</p>
          </div>
        </div>

        {/* Equalizer */}
        <div className="mt-4 flex items-end justify-between gap-[2px] h-9">
          {levels.map((lv, i) => (
            <span
              key={i}
              className="flex-1 rounded-full bg-white"
              style={{ height: `${Math.max(5, lv * 100)}%`, transition: "height 90ms linear", opacity: 0.55 + lv * 0.45 }}
            />
          ))}
        </div>

        {/* Controls */}
        <div className="mt-4 flex items-center gap-3 sm:gap-4">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={isPlaying ? "Pauze" : "Afspelen"}
            className="group relative flex h-12 w-12 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-white text-[#1E6BE6] shadow-lg shadow-blue-900/25 transition-transform duration-200 hover:scale-105 active:scale-95"
          >
            {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : isPlaying ? <Pause className="h-6 w-6" fill="currentColor" /> : <Play className="h-6 w-6 ml-0.5" fill="currentColor" />}
          </button>

          <div className="flex flex-1 items-center gap-3">
            <button type="button" onClick={toggleMute} aria-label={isMuted ? "Geluid aan" : "Dempen"} className="text-white/80 hover:text-white transition-colors">
              {isMuted || volume === 0 ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </button>
            <Slider value={[isMuted ? 0 : Math.round(volume * 100)]} max={100} step={1} onValueChange={(v) => setVolume(v[0] / 100)} className="vol-slider-light flex-1" aria-label="Volume" />
          </div>
        </div>

        {error && <p className="mt-3 text-center text-xs text-red-100">{error}</p>}
      </div>
    </div>
  );
};

export default RadioPlayer;
